'use client';

import {
  useEffect,
  useRef,
  useCallback,
  memo,
  ReactNode,
  CSSProperties,
} from 'react';
import { useWindowSize } from 'hooks/dom/useWindowSize';
import { PHONE_MAX_WIDTH_IN_PX } from '..';
import styles from './Tabs.module.css';

export interface TabItem {
  title: string;
  icon?: string | ReactNode;
  value: string;
}

export interface TabsProps {
  tabs: TabItem[];
  value?: string;
  onChange: (value: string) => void;
  className?: string;
  compactSm?: boolean;
}

const TabComponent = memo(
  ({
    tab,
    isActive,
    isMobile,
    compactSm,
    onClick,
    tabRef,
  }: {
    tab: TabItem;
    isActive: boolean;
    isMobile: boolean;
    compactSm: boolean;
    onClick: () => void;
    tabRef: (el: HTMLButtonElement | null) => void;
  }) => {
    const tabWidth =
      isMobile && !compactSm ? 'auto' : isActive ? '149.5px' : '48px';

    return (
      <button
        key={tab.value}
        className={styles.tab}
        style={{ '--tab-width': tabWidth } as CSSProperties}
        onClick={onClick}
        ref={tabRef}
      >
        {tab.icon && (
          <div className={styles.icon}>
            {typeof tab.icon === 'string' ? (
              <img
                src={tab.icon}
                alt={tab.title}
                width={isMobile ? 14 : 16}
                height={isMobile ? 14 : 16}
              />
            ) : (
              tab.icon
            )}
          </div>
        )}
        {!isMobile || !compactSm || (isActive && tab.title) ? (
          <span>{tab.title}</span>
        ) : null}
      </button>
    );
  },
);

TabComponent.displayName = 'TabComponent';

export const Tabs = memo(
  ({ tabs, value, onChange, className, compactSm = true }: TabsProps) => {
    const FloaterRef = useRef<HTMLSpanElement | null>(null);
    const TabsRootRef = useRef<HTMLDivElement | null>(null);
    const tabRefs = useRef<(HTMLButtonElement | null)[]>([]);
    const { width } = useWindowSize();
    const isMobile = width < PHONE_MAX_WIDTH_IN_PX;

    const currentIndex = value
      ? tabs.findIndex((tab) => tab.value === value)
      : 0;

    useEffect(() => {
      tabRefs.current = tabRefs.current.slice(0, tabs.length);
    }, [tabs.length]);

    const updateFloaterPosition = useCallback(
      (targetIndex: number) => {
        if (!FloaterRef.current || !TabsRootRef.current) return;

        const tabElements = tabRefs.current.filter(Boolean);
        const rootRect = TabsRootRef.current.getBoundingClientRect();

        if (
          targetIndex >= 0 &&
          targetIndex < tabElements.length &&
          tabElements[targetIndex]
        ) {
          const targetTab = tabElements[targetIndex];
          if (!targetTab) return;

          const targetRect = targetTab.getBoundingClientRect();

          if (isMobile) {
            if (!compactSm) {
              // For non-compact mobile, calculate based on cumulative tab positions
              // but with proper margin accounting for FloaterSpan margin: 0 4px
              const floaterWidth = targetRect.width - 8; // Account for FloaterSpan internal padding
              const floaterHeight = targetRect.height - 8;

              // Calculate left position based on tab positions within the container
              // The FloaterSpan margin: 0 4px means it has 4px margin on each side
              let leftPosition = 0;
              const tabGap = 4; // Gap between tabs

              // Sum up the widths of all tabs before the target
              for (let i = 0; i < targetIndex; i++) {
                const tabElement = tabRefs.current[i];
                if (tabElement) {
                  leftPosition +=
                    tabElement.getBoundingClientRect().width + tabGap;
                }
              }

              FloaterRef.current.style.width = `${floaterWidth}px`;
              FloaterRef.current.style.height = `${floaterHeight}px`;
              FloaterRef.current.style.left = `${leftPosition}px`;
            } else {
              // Original compact behavior
              // Account for FloaterSpan margin: 0 4px (8px total)
              const activeTabWidth = 149.5 - 8; // Subtract 8px for left+right margin
              const floaterHeight = targetRect.height - 8;

              let leftPosition = 0;
              const tabGap = 4; // Gap between tabs (from TabsBar gap: 4px)
              const inactiveTabWidth = 48;

              if (targetIndex === 0) {
                leftPosition = 0;
              } else {
                for (let i = 0; i < targetIndex; i++) {
                  leftPosition += inactiveTabWidth + tabGap;
                }
              }

              FloaterRef.current.style.width = `${activeTabWidth}px`;
              FloaterRef.current.style.height = `${floaterHeight}px`;
              FloaterRef.current.style.left = `${leftPosition}px`;
            }
          } else {
            const floaterWidth = targetRect.width - 8;
            const floaterHeight = targetRect.height - 8;
            const leftPosition = targetRect.left - rootRect.left + 4;

            FloaterRef.current.style.width = `${floaterWidth}px`;
            FloaterRef.current.style.height = `${floaterHeight}px`;
            FloaterRef.current.style.left = `${leftPosition}px`;
          }
        }
      },
      [isMobile, compactSm],
    );

    useEffect(() => {
      const timeoutId = setTimeout(() => {
        updateFloaterPosition(currentIndex);
      }, 50);

      return () => clearTimeout(timeoutId);
    }, [currentIndex, width, updateFloaterPosition]);

    useEffect(() => {
      const handleResize = () => {
        updateFloaterPosition(currentIndex);
      };

      window.addEventListener('resize', handleResize);
      return () => window.removeEventListener('resize', handleResize);
    }, [currentIndex, updateFloaterPosition]);

    const handleTabClick = useCallback(
      (tabValue: string, index: number) => {
        updateFloaterPosition(index);
        onChange(tabValue);
      },
      [onChange, updateFloaterPosition],
    );

    return (
      <div className={[styles.container, className].filter(Boolean).join(' ')}>
        <div className={styles.bar} ref={TabsRootRef} data-compact-sm={compactSm}>
          <span className={styles.floater} ref={FloaterRef} />
          {tabs.map((tab, index) => (
            <TabComponent
              key={tab.value}
              tab={tab}
              isActive={index === currentIndex}
              isMobile={isMobile}
              compactSm={compactSm}
              onClick={() => handleTabClick(tab.value, index)}
              tabRef={(el) => {
                tabRefs.current[index] = el;
              }}
            />
          ))}
        </div>
      </div>
    );
  },
);

Tabs.displayName = 'Tabs';

export const TabsSection = ({
  tabs_data,
  currentIndex,
  goToSlide,
}: {
  tabs_data: any[];
  currentIndex: number;
  goToSlide: (index: number) => void;
}) => {
  const mappedTabs: TabItem[] = tabs_data.map((tab, index) => ({
    title: tab.text || `Tab ${index + 1}`,
    icon: tab.icon,
    value: tab.value || index.toString(),
  }));

  const currentValue = mappedTabs[currentIndex]?.value || '0';

  const handleChange = (value: string) => {
    const newIndex = mappedTabs.findIndex((tab) => tab.value === value);
    if (newIndex !== -1) {
      goToSlide(newIndex);
    }
  };

  return (
    <Tabs tabs={mappedTabs} value={currentValue} onChange={handleChange} />
  );
};
